import { useTranslation } from 'react-i18next'
import { X } from '@digital-twins/design-system/icons'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type {
  SocialTermNoteTone,
  SocialTermsInputProps,
} from '@/types/components/social-tracker-dialog'
import { addSocialTerms } from './socialTrackerForm'

const NOTE_TONE_CLASSES: Record<SocialTermNoteTone, string> = {
  success: 'bg-chart-positive',
  destructive: 'bg-destructive',
  muted: 'bg-muted-foreground/60',
  pending: 'animate-pulse bg-muted-foreground/60',
}

export interface SocialNoteDotProps {
  tone: SocialTermNoteTone
  className?: string
}

/** A small status dot (a source check): decorative, its label is said beside it. */
export function SocialNoteDot(props: SocialNoteDotProps) {
  const { tone, className } = props

  return (
    <span
      className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', NOTE_TONE_CLASSES[tone], className)}
      data-tone={tone}
      aria-hidden="true"
    />
  )
}

/** Draft text belongs to the wizard, so disclosure and step navigation cannot lose it. */
export function SocialTermsInput({
  id,
  values,
  draft,
  onDraftChange,
  onChange,
  placeholder,
  testId,
  isInvalid,
  describedBy,
  isInline = true,
  notes,
  noteTones,
}: SocialTermsInputProps) {
  const { t } = useTranslation('app')
  function commit() {
    if (!draft.trim()) return
    onChange(addSocialTerms(values, draft))
    onDraftChange('')
  }
  const input = (
    <Input
      id={id}
      value={draft}
      className={cn(
        'min-w-[100px] flex-1 basis-24 border-0 px-0 shadow-none focus-visible:ring-0',
        isInline ? 'h-7' : 'h-9',
      )}
      placeholder={values.length ? undefined : placeholder}
      aria-invalid={isInvalid || undefined}
      aria-describedby={describedBy}
      data-testid={testId}
      onChange={(event) => onDraftChange(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return
        if (event.key === 'Enter' || event.key === ',') {
          event.preventDefault()
          commit()
        }
      }}
    />
  )
  const chips =
    values.length > 0 ? (
      <ul
        className={
          isInline
            ? 'contents'
            : 'ml-auto flex max-h-28 max-w-full flex-wrap justify-end gap-1.5 overflow-y-auto'
        }
        data-testid={`${testId}-values`}
      >
        {values.map((value) => {
          const note = notes?.[value]
          return (
            <li
              key={value}
              className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-sm text-foreground"
            >
              {note ? (
                // The dot says the check at a glance; its label is the tooltip and what a screen reader hears.
                <span
                  className="inline-flex shrink-0 items-center"
                  title={note}
                  data-testid={`${testId}-note-${value}`}
                >
                  <SocialNoteDot tone={noteTones?.[value] ?? 'muted'} />
                  <span className="sr-only">{note}</span>
                </span>
              ) : null}
              <span className="truncate">{value}</span>
              <button
                type="button"
                className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('socialListening.form.removeTermAria', {
                  term: value,
                })}
                data-testid={`${testId}-remove-${value}`}
                onClick={() =>
                  onChange(values.filter((item) => item !== value))
                }
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          )
        })}
      </ul>
    ) : null
  return (
    <div
      className={
        isInline
          ? 'flex min-h-9 flex-wrap items-center justify-start gap-1.5 rounded-md border border-input bg-background px-2.5 py-1 focus-within:border-primary/45'
          : 'flex min-h-11 flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-input bg-background px-3 py-1 focus-within:border-primary/45'
      }
    >
      {isInline ? chips : input}
      {isInline ? input : chips}
    </div>
  )
}
